export const BULK_AVATAR_STATUS = {
  UPLOADED: 'UPLOADED',
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
  UNSUPPORTED_TYPE: 'UNSUPPORTED_TYPE',
  TOO_LARGE: 'TOO_LARGE',
  DUPLICATE: 'DUPLICATE',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
} as const;

export type BulkAvatarStatus = (typeof BULK_AVATAR_STATUS)[keyof typeof BULK_AVATAR_STATUS];

export interface UserWithEmails {
  id: string;
  email?: string;
  secondaryEmails?: { email: string }[];
}

export interface UploadFailure {
  status: BulkAvatarStatus;
  message?: string;
}

const AVATAR_UPLOAD_REJECTED_CODE = 'AVATAR_UPLOAD_REJECTED';

const HTTP_PAYLOAD_TOO_LARGE = 413;

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

function isJunkEntry(path: string): boolean {
  if (path.endsWith('/')) return true;
  const segments = path.split('/');
  if (segments.includes('__MACOSX')) return true;
  const baseName = segments[segments.length - 1] ?? '';
  return baseName.startsWith('.');
}

export function mimeTypeForFileName(fileName: string): string | undefined {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension ? MIME_BY_EXTENSION[extension] : undefined;
}

function emailFromFileName(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return (dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName).trim().toLowerCase();
}

// Email-named non-images stay visible as UNSUPPORTED_TYPE result rows; other
// stray files (readme.txt, Thumbs.db) drop silently along with archive junk.
export function isCandidateEntry(path: string): boolean {
  if (isJunkEntry(path)) return false;
  const fileName = path.split('/').pop() ?? '';
  if (mimeTypeForFileName(fileName)) return true;
  return emailFromFileName(fileName).includes('@');
}

// Secondary aliases first so primary emails overwrite them — a photo named
// with an address that is both always lands on the account that owns it.
export function buildEmailToUserIdMap(
  users: UserWithEmails[] | null | undefined,
): Map<string, string> {
  const list = users ?? [];
  return [
    ...list.flatMap(user =>
      (user.secondaryEmails ?? []).map(({ email }): [string | undefined, string] => [
        email,
        user.id,
      ]),
    ),
    ...list.map((user): [string | undefined, string] => [user.email, user.id]),
  ]
    .filter((pair): pair is [string, string] => !!pair[0])
    .reduce((map, [email, id]) => map.set(email.toLowerCase(), id), new Map<string, string>());
}

export function classifyEntry({
  fileName,
  seenEmails,
  emailToUserId,
}: {
  fileName: string;
  seenEmails: Set<string>;
  emailToUserId: Map<string, string>;
}): { email: string; userId?: string; status: BulkAvatarStatus | null } {
  const email = emailFromFileName(fileName);

  if (!mimeTypeForFileName(fileName)) {
    return { email, status: BULK_AVATAR_STATUS.UNSUPPORTED_TYPE };
  }
  const userId = emailToUserId.get(email);
  if (!userId) {
    return { email, status: BULK_AVATAR_STATUS.EMAIL_NOT_FOUND };
  }
  if (seenEmails.has(email)) {
    return { email, status: BULK_AVATAR_STATUS.DUPLICATE };
  }

  seenEmails.add(email);
  return { email, userId, status: null };
}

export function mapUploadFailure({
  data,
  error,
}: {
  data?: { code?: string; message?: string };
  error?: { response?: { status?: number }; message?: string };
}): UploadFailure {
  if (!error?.response) {
    return { status: BULK_AVATAR_STATUS.NETWORK_ERROR };
  }
  if (error.response.status === HTTP_PAYLOAD_TOO_LARGE) {
    return { status: BULK_AVATAR_STATUS.TOO_LARGE };
  }
  if (data?.code === AVATAR_UPLOAD_REJECTED_CODE) {
    return { status: BULK_AVATAR_STATUS.UNSUPPORTED_TYPE, message: data?.message };
  }
  return { status: BULK_AVATAR_STATUS.UPLOAD_FAILED, message: data?.message };
}
